import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, ChevronLeft } from 'lucide-react';
import { FlowButton } from 'components/ui/flow-button';
import { getDefaultSiteContent, getPublicSiteContent } from 'lib/siteContentCms';
import { submitPartnerApplication } from 'lib/sitePublicApi';
import './PartnerApplication.css';

const TYPES = [
  ['referral', 'Referral or affiliate partner', 'partnerReferral'],
  ['vendor', 'Vendor (insurance, factoring, ELD and others)', 'partnerVendor'],
  ['speaker', 'Course speaker', 'partnerSpeaker'],
  ['reseller', 'Reseller', 'partnerReseller'],
];
const STEPS = [
  { title: 'Partner type', helper: 'Choose how you would like to work with Magellan.' },
  { title: 'Contact details', helper: 'Tell us who you are and where we can reach you.' },
  { title: 'Your proposal', helper: 'Tell us about the work you would contribute and the people it would help.' },
  { title: 'Review and send', helper: 'Check your details and read the partner terms before sending your request.' },
];
const CONTACT_FIELDS = [
  ['name', 'Full name', true, 'text', 'name'],
  ['email', 'Email', true, 'email', 'email'],
  ['company', 'Company', true, 'text', 'organization'],
  ['phone', 'Phone', false, 'tel', 'tel'],
  ['website', 'Website', false, 'text', 'url'],
  ['country', 'Country', false, 'text', 'country-name'],
];
const parseQuestions = block => String(block.body || '').split('\n').map(q => q.trim()).filter(Boolean).slice(0, 20);

function initialQuestionnaires() {
  return Object.fromEntries(TYPES.map(([id, , key]) => [id, {
    questions: parseQuestions(getDefaultSiteContent(key)), answers: {}, started: false, loading: true, fallback: false,
  }]));
}

export default function PartnerApplication({ partnerType, onTypeChange }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', website: '', country: '', vendorCategory: '', message: '' });
  const [questionnaires, setQuestionnaires] = useState(initialQuestionnaires);
  const [currentStep, setCurrentStep] = useState(0);
  const [maxUnlockedStep, setMaxUnlockedStep] = useState(0);
  const [consent, setConsent] = useState(false);
  const [terms, setTerms] = useState({ loading: true });
  const [termsAttempt, setTermsAttempt] = useState(0);
  const [state, setState] = useState({});
  const headingRef = useRef(null);
  const focusStep = useRef(false);
  const submitting = useRef(false);
  const previousType = useRef(partnerType);
  const questionnaire = questionnaires[partnerType];
  const typeLabel = TYPES.find(([id]) => id === partnerType)[1];
  const answers = questionnaire.questions.map(question => ({ question, answer: questionnaire.answers[question] || '' }));
  const progress = state.done ? 100 : Math.round((currentStep + 1) / STEPS.length * 100);

  // Load every type once for this form. Switching types never starts a request.
  // Existing defaults are usable immediately, including when the CMS is offline.
  useEffect(() => {
    let active = true;
    TYPES.forEach(([id, , key]) => {
      getPublicSiteContent(key).then(block => {
        if (!active) return;
        const loadedQuestions = parseQuestions(block);
        setQuestionnaires(current => ({ ...current, [id]: {
          ...current[id],
          // Keep a stable question set once someone has begun answering it.
          questions: current[id].started || !loadedQuestions.length ? current[id].questions : loadedQuestions,
          loading: false,
          fallback: block.__source === 'emergency-default' || !loadedQuestions.length,
        } }));
      }).catch(() => {
        if (active) setQuestionnaires(current => ({ ...current, [id]: { ...current[id], loading: false, fallback: true } }));
      });
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    let active = true;
    setTerms({ loading: true });
    fetch('/api/site/v1/legal/partner-terms', { credentials: 'omit', cache: 'no-store', signal: controller.signal })
      .then(response => response.ok ? response.json() : null)
      .then(result => {
        if (active) setTerms(result?.data?.body?.trim() ? { body: result.data.body } : { error: true });
      })
      .catch(() => { if (active) setTerms({ error: true }); })
      .finally(() => clearTimeout(timer));
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [termsAttempt]);

  useEffect(() => {
    if (previousType.current === partnerType) return;
    // A path CTA above the form cannot change a request already being sent.
    if (submitting.current) { onTypeChange(previousType.current); return; }
    previousType.current = partnerType;
    setCurrentStep(0);
    setMaxUnlockedStep(0);
    setConsent(false);
    setState({});
  }, [partnerType, onTypeChange]);

  useEffect(() => {
    if (!focusStep.current) return;
    focusStep.current = false;
    headingRef.current?.focus({ preventScroll: true });
    headingRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
  }, [currentStep, state.done]);

  const set = key => event => {
    const value = event.target.value;
    setForm(current => ({ ...current, [key]: value }));
  };
  function goToStep(index) {
    if (submitting.current || index > maxUnlockedStep) return;
    setState({});
    focusStep.current = true;
    setCurrentStep(index);
  }
  function contactValid() {
    const missing = CONTACT_FIELDS.find(([key, , required]) => required && !form[key].trim());
    if (missing) {
      setState({ error: `Enter your ${missing[1].toLowerCase()} before continuing.` });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setState({ error: 'Enter a valid email address before continuing.' });
      return false;
    }
    return true;
  }
  async function submit(event) {
    event.preventDefault();
    if (submitting.current) return;
    if (currentStep === 1 && !contactValid()) return;
    if (currentStep < STEPS.length - 1) {
      const next = currentStep + 1;
      setMaxUnlockedStep(unlocked => Math.max(unlocked, next));
      focusStep.current = true;
      setCurrentStep(next);
      setState({});
      return;
    }
    if (!contactValid()) {
      focusStep.current = true;
      setCurrentStep(1);
      return;
    }
    if (!terms.body) { setState({ error: 'Load the partner terms before sending your request.' }); return; }
    if (!consent) { setState({ error: 'Accept the partner terms to continue.' }); return; }
    submitting.current = true;
    setState({ busy: true });
    try {
      const result = await submitPartnerApplication({ ...form, partnerType, answers, consentAccepted: true });
      focusStep.current = Boolean(result.success);
      setState(result.success ? { done: true } : { error: result.error || 'Your request could not be sent. Please try again.' });
    } catch {
      setState({ error: 'Your request could not be sent. Please try again.' });
    } finally {
      submitting.current = false;
    }
  }

  return <div className="partner-wizard">
    <aside className="partner-wizard-progress" aria-label="Application progress">
      <p className="partner-wizard-eyebrow">Application progress</p>
      <div className="partner-wizard-track" role="progressbar" aria-label="Application steps" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
        <span style={{ width: `${progress}%` }} />
      </div>
      <p className="partner-wizard-percent">{progress}%</p>
      <nav aria-label="Partner application steps">
        {STEPS.map((step, index) => <button key={step.title} type="button" onClick={() => goToStep(index)}
          disabled={index > maxUnlockedStep || state.busy || state.done} aria-current={index === currentStep ? 'step' : undefined}>
          <span className="partner-wizard-eyebrow">Step 0{index + 1}</span><span>{step.title}</span>
        </button>)}
      </nav>
    </aside>

    {state.done ? <div className="partner-wizard-panel partner-wizard-success" role="status" data-testid="partner-application-success">
      <CheckCircle2 size={42} aria-hidden="true" />
      <h3 ref={headingRef} tabIndex={-1}>Partner request received.</h3>
      <p>Thank you. We received your partner request and will contact you.</p>
    </div> : <form onSubmit={submit} className="partner-wizard-panel" data-testid="partner-application-form" aria-labelledby="partner-step-heading">
      <fieldset disabled={state.busy}>
        <div className="partner-wizard-heading">
          <p className="partner-wizard-eyebrow">Step 0{currentStep + 1} / 04</p>
          <h3 id="partner-step-heading" ref={headingRef} tabIndex={-1}>{STEPS[currentStep].title}</h3>
          <p>{STEPS[currentStep].helper}</p>
        </div>

        {currentStep === 0 && <div className="partner-wizard-fields">
          <label className="partner-wizard-field" htmlFor="partner-type"><span>Partner type</span>
            <select id="partner-type" value={partnerType} onChange={event => onTypeChange(event.target.value)}>
              {TYPES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </label>
          {partnerType === 'vendor' && <label className="partner-wizard-field" htmlFor="partner-vendor-category"><span>What do you offer (insurance, factoring, ELD, other)?</span>
            <input id="partner-vendor-category" maxLength={200} value={form.vendorCategory} onChange={set('vendorCategory')} />
          </label>}
          <div className="partner-wizard-preview" aria-live="polite" aria-atomic="true">
            <p className="partner-wizard-eyebrow">Your proposal questions</p>
            <p className="partner-wizard-preview-type">{typeLabel}</p>
            <ul>{questionnaire.questions.map(question => <li key={question}>{question}</li>)}</ul>
          </div>
          <p className="partner-wizard-note">Your answers stay here as you move between steps or partner types.</p>
        </div>}

        {currentStep === 1 && <div className="partner-wizard-contact-grid">
          {CONTACT_FIELDS.map(([key, label, required, type, autoComplete]) => <label key={key} className="partner-wizard-field" htmlFor={`partner-${key}`}>
            <span>{label}{required ? ' *' : ''}</span>
            <input id={`partner-${key}`} type={type} autoComplete={autoComplete} required={required} maxLength={300} value={form[key]} onChange={set(key)} />
          </label>)}
        </div>}

        {currentStep === 2 && <div className="partner-wizard-fields">
          <p className="partner-wizard-type-label">{typeLabel}</p>
          {questionnaire.questions.map((question, index) => <label className="partner-wizard-field" key={question} htmlFor={`partner-question-${index}`}>
            <span>{question}</span>
            <textarea id={`partner-question-${index}`} rows={3} maxLength={5000} value={questionnaire.answers[question] || ''}
              onChange={event => {
                const value = event.target.value;
                setQuestionnaires(current => ({ ...current, [partnerType]: { ...current[partnerType], started: true,
                  answers: { ...current[partnerType].answers, [question]: value } } }));
              }} />
          </label>)}
          <label className="partner-wizard-field" htmlFor="partner-message"><span>Anything else</span>
            <textarea id="partner-message" rows={4} maxLength={5000} value={form.message} onChange={set('message')} />
          </label>
        </div>}

        {currentStep < 3 && <p className="partner-wizard-note" role="status">
          {questionnaire.loading ? 'Checking for updated questions. You can continue with the questions shown.' : questionnaire.fallback ? 'Showing the standard questions for this partner type.' : ''}
        </p>}

        {currentStep === 3 && <div className="partner-wizard-fields">
          <div className="partner-wizard-review-section">
            <div className="partner-wizard-review-heading"><h4>Your details</h4><button type="button" onClick={() => goToStep(1)}>Edit contact details</button></div>
            <dl className="partner-wizard-review-grid">
              <div><dt>Partner type</dt><dd>{typeLabel}</dd></div>
              {CONTACT_FIELDS.map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{form[key] || 'Not provided'}</dd></div>)}
              {partnerType === 'vendor' && <div><dt>What you offer</dt><dd>{form.vendorCategory || 'Not provided'}</dd></div>}
            </dl>
          </div>
          <div className="partner-wizard-review-section">
            <div className="partner-wizard-review-heading"><h4>Your proposal</h4><button type="button" onClick={() => goToStep(2)}>Edit proposal</button></div>
            <dl className="partner-wizard-review-answers">
              {answers.map(({ question, answer }) => <div key={question}><dt>{question}</dt><dd>{answer || 'Not provided'}</dd></div>)}
              <div><dt>Anything else</dt><dd>{form.message || 'Not provided'}</dd></div>
            </dl>
          </div>
          <div className="partner-wizard-terms">
            <h4>Partner terms</h4>
            {terms.body ? <p className="partner-wizard-terms-body">{terms.body}</p> : <div role="status">
              <p>{terms.loading ? 'Loading partner terms…' : 'Partner terms could not be loaded. Please retry before sending your request.'}</p>
              {!terms.loading && <button type="button" onClick={() => setTermsAttempt(attempt => attempt + 1)}>Retry partner terms</button>}
            </div>}
            <label className="partner-wizard-consent"><input type="checkbox" checked={consent} disabled={!terms.body}
              onChange={event => setConsent(event.target.checked)} /><span>I accept the partner terms.</span></label>
          </div>
        </div>}

        {state.error && <p className="partner-wizard-error" role="alert">{state.error}</p>}
        <div className="partner-wizard-actions">
          <FlowButton type="button" showArrow={false} onClick={() => goToStep(currentStep - 1)} disabled={currentStep === 0}>
            <ChevronLeft size={16} aria-hidden="true" />Back
          </FlowButton>
          <FlowButton type="submit" disabled={state.busy || (currentStep === 3 && !terms.body)}>
            {state.busy ? 'Sending…' : currentStep === 3 ? 'Send partner request' : 'Continue'}
          </FlowButton>
        </div>
      </fieldset>
    </form>}
  </div>;
}
